import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  CreateUserRequestFormSchema,
  UpdateAdministrationUserDto,
  UpdateUserRequestFormSchema,
  UserAdministrationDto,
  UserTeamDto,
} from '@act/shared/data-transfer-objects';
import { assignableRoles, UserRole } from '@act/shared/definitions';
import { useForm } from 'react-hook-form';
import { FlexContainer, FormBuilder } from '@act/storybook';
import EditUserHeader from '../EditUserHeader/EditUserHeader';
import { useAppDispatch } from 'redux/store-hooks';
import {
  createUser,
  deleteUser,
  updateUser,
  userAdminSelectors,
  getUsers,
} from 'redux/slices/userAdminSlice';
import { clientAppSelectors, getAllClients } from 'redux/slices/clientSlice';
import { getTeams, userAppSelectors } from 'redux/slices/usersSlice';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { useParams } from 'react-router-dom';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateUserPageContainer,
  FieldSection,
  ResponsiveFieldSection,
} from './Styles';
import ManageTeams from './ManageTeams';
import { TeamRole } from '@act/shared/types';
import ManageClients from './ManageClients';
import { ClientAuditorDto } from '@act/shared/data-transfer-objects';
import { stringFormatter } from 'utilities/formatters/stringFormatter';
import VoicemailForm from './VoicemailForm';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import { userAdministratorRequests } from 'redux/requests/user-administrator.requests';
import { useApiCall } from 'redux/utils/api-hook';
import EditAvon, { AvonFields } from './EditAvon';
import { useToast } from 'react-components/app/ReusableToast';
import { useUserFormConfig } from './useUserFormConfig';
import { InternalUserForm } from './types';

const defaultFormValues: InternalUserForm = {
  userId: '',
  firstName: '',
  lastName: '',
  role: '',
  email: '',
  mobilePhone: '',
  resourcingNumberBase: '',
  timeZone: '',
  teamIds: [],
  managedClients: [],
  availablePhoneNumbers: null,
  resourceNumber: null,
  hasAppAccess: false,
};

const defaultAvonDetails: AvonFields = {
  specialization: '',
  medical_centers: [],
  userType: null,
};

const ALLOWED_TEAM_ROLES = [TeamRole.MEMBER, TeamRole.VIEWER];

const roleOptions = ALLOWED_TEAM_ROLES.map((role, index) => {
  return {
    key: index,
    label: TeamRole[role],
    value: role,
  };
});

export const CreateEditUserPage = () => {
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const { userId } = useParams();
  const user: UserAdministrationDto =
    userAdminSelectors.useSelectAdminUserById(userId);
  const dispatch = useAppDispatch();
  const navigate = useAppNavigation();
  const toast = useToast();
  const allTeams = userAppSelectors.useSelectAllTeams();
  const allClients = clientAppSelectors.useSelectAllClients();
  const [
    availablePhoneNumbers,
    availablePhoneNumbersLoading,
    getAvailablePhoneNumbers,
  ] = useApiCall(userAdministratorRequests.getAvailablePhoneNumbers);
  const [avonSpecializations, _specializationsLoading, getAvonSpecializations] =
    useApiCall(userAdministratorRequests.getAvonSpecializations);
  const [avonMedicalCenters, _medicalCentersLoading, getAvonMedicalCenters] =
    useApiCall(userAdministratorRequests.getAvonMedicalCenters);

  const [avonUser, isLoadingAvonUser, getAvonUser] = useApiCall(
    userAdministratorRequests.getAvonUser
  );
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const isAssignableRole: boolean = user
    ? assignableRoles.includes(user.role)
    : true;
  const isAdmin =
    !!user && [UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user?.role);
  let initialFormState: InternalUserForm = defaultFormValues;
  const [selectedTeams, setSelectedTeams] = useState<UserTeamDto[]>([]);
  const [selectedClients, setSelectedClients] = useState<ClientAuditorDto[]>(
    []
  );
  const [shouldCreateAvonUser, setShouldCreateAvonUser] =
    useState<boolean>(false);
  const [avonUserDetails, setAvonUserDetails] =
    useState<AvonFields>(defaultAvonDetails);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(!!userId);
  const [showAvailableNumbers, setShowAvailableNumbers] =
    useState<boolean>(false);
  const [currentResourceNumber, setCurrentResourceNumber] =
    useState<string>(null);

  useEffect(() => {
    dispatch(getUsers({}));
    if (!avonSpecializations) {
      void getAvonSpecializations({ params: null });
    }
    if (!avonMedicalCenters) {
      void getAvonMedicalCenters({ params: null });
    }
    if (!allTeams) {
      dispatch(getTeams({}));
    }
    if (!allClients) {
      dispatch(getAllClients({}));
    }
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState,
    getValues,
    watch,
    setError,
    setValue,
    clearErrors,
  } = useForm<InternalUserForm>({
    resolver: zodResolver(
      isEditing ? UpdateUserRequestFormSchema : CreateUserRequestFormSchema
    ) as any, // TODO: Fix schema/form type mismatch - schemas have different field requirements than InternalUserForm
    defaultValues: initialFormState,
  });

  const handleFormErrors = errors => {
    const fieldKeys = Object.keys(errors);
    if (fieldKeys?.length > 0) {
      setFormErrors(fieldKeys.map(key => errors[key]?.message));
    }
  };

  useEffect(() => {
    if (user) {
      setSelectedTeams(user?.teams);
      setSelectedClients(user?.managedClients);
      initialFormState = {
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        mobilePhone: user?.mobilePhone?.number,
        resourcingNumberBase: user.resourcingNumber?.number,
        timeZone: user?.timeZone,
        teamIds: user?.teams,
        managedClients: user?.managedClients,
        ...(isAssignableRole ? { role: user.role } : {}),
        hasAppAccess: user.hasAppAccess,
      };
      reset(initialFormState);
      if (user?.avonId && !avonUser) {
        void getAvonUser({
          params: { avonId: user.avonId },
        });
      }
    }
  }, [user]);

  useEffect(() => {
    dispatch(getTeams({}));
    dispatch(getAllClients({}));
  }, [dispatch]);

  useEffect(() => {
    if (!!avonUser && !isLoadingAvonUser) {
      setAvonUserDetails({
        specialization: avonUser?.specialization || null,
        medical_centers: avonUser?.medical_centers?.map(
          center => `mctr_${center}`
        ),
        userType: avonUser?.userType,
      });
    }
  }, [avonUser, isLoadingAvonUser]);

  const resetAndNavigate = () => {
    setSelectedClients([]);
    setSelectedTeams([]);
    setFormErrors([]);
    reset(defaultFormValues);
    setIsEditing(false);
    navigate.toUserCreation();
  };

  const handleSubmitPressed = async data => {
    setFormErrors([]);
    const teams = selectedTeams
      ? selectedTeams.map(team => ({
          teamId: team.teamId,
          isPrimary: team.role === TeamRole.MEMBER,
          isManager: team.isManager,
          startDate: team.startDate,
          endDate: team.endDate,
        }))
      : [];
    let clientIds = [];
    const role = data['role'];
    // Only set client auditor associations if user is a Guide Supervisor
    if (role === UserRole.GUIDE_SUPERVISOR) {
      clientIds = selectedClients.map(client => ({
        clientId: client.clientId,
        startDate: client.startDate,
        endDate: client.endDate,
      }));
    }
    const values = {
      firstName: stringFormatter.capitalize(data['firstName']),
      lastName: stringFormatter.capitalize(data['lastName']),
      role: role,
      email: data['email'],
      resourceNumber: data['resourceNumber'],
      timeZone: data['timeZone'],
      mobilePhone: data['mobilePhone'],
      teams: teams,
      clientAuditors: clientIds,
      // Only include Avon fields if we're creating a new user (temporarily disable updates)
      ...(shouldCreateAvonUser
        ? {
            specialization: avonUserDetails.specialization,
            medical_centers: avonUserDetails.medical_centers,
          }
        : {}),
      createAvonUser: shouldCreateAvonUser,
      hasAppAccess: data['hasAppAccess'],
    };

    // Create or edit User
    let response;
    if (user && isEditing) {
      // Do not provide role field to update request if not assignable
      if (!isAssignableRole) {
        delete values[role];
      }
      const updateBody: UpdateAdministrationUserDto = {
        ...values,
      };
      response = await dispatch(
        updateUser({
          params: { userId: user.id },
          body: updateBody,
        })
      );
    } else {
      response = await dispatch(
        createUser({
          body: {
            ...values,
          },
        })
      );
    }
    if (response?.error) {
      const message = response?.payload.body.userMessage || 'An error occurred';
      if (message.includes('email')) {
        setError('email', {
          type: 'value',
          message: stringFormatter.capitalize(message),
        });
      }
      setFormErrors([message]);
      toast(stringFormatter.capitalize(message), 'error');
    } else {
      toast(`User ${isEditing ? 'updated' : 'created'} successfully!`);
      resetAndNavigate();
    }
  };

  const handleDeletePressed = async () => {
    if (user) {
      const shouldDelete = await openConfirmationAlertDialog({
        title: 'Warning',
        message: 'Are you sure you want to delete this user?',
        confirmationText: 'Yes, delete now',
        hideIcon: true,
      });
      if (shouldDelete) {
        await dispatch(deleteUser({ params: { userId: user.id } }));
        toast('User deleted successfully!');
        resetAndNavigate();
      }
    }
  };

  const currentRole = watch('role');
  const isGuideOrProvider =
    [UserRole.GUIDE, UserRole.PROVIDER].includes(UserRole[currentRole]) ||
    !currentRole;
  const hasAvonUser = isEditing && !!user?.avonId;

  const resourcingNumberBaseWatch = watch('resourcingNumberBase');
  const resourceNumberWatch = watch('resourceNumber');
  useEffect(() => {
    setShowAvailableNumbers(false);
    if (resourcingNumberBaseWatch) {
      clearErrors('resourcingNumberBase');
    }
    if (getValues('resourceNumber')) {
      setValue('resourceNumber', null);
    }
  }, [resourcingNumberBaseWatch]);

  useEffect(() => {
    if (resourceNumberWatch === currentResourceNumber) {
      clearErrors('resourceNumber');
    } else {
      setValue('resourceNumber', null);
    }
  }, [resourceNumberWatch]);

  const getNumbers = () => {
    const baseNumber = getValues('resourcingNumberBase');
    if (!baseNumber || baseNumber.length !== 3) {
      setError('resourcingNumberBase', {
        type: 'value',
        message: 'Resourcing Number is required and must be 3 digits',
      });
      return;
    }
    clearErrors('resourcingNumberBase');
    setValue('resourceNumber', null);

    void getAvailablePhoneNumbers({
      params: { baseNumber: Number(baseNumber) },
    });
    setShowAvailableNumbers(true);
  };

  // Form configuration for FormBuilder
  const formConfig = useUserFormConfig({
    isEditing,
    userRoleString: currentRole,
    isAssignableRole,
    shouldCreateAvonUser,
    hasAvonUser,
    availablePhoneNumbers,
    availablePhoneNumbersLoading,
    showAvailableNumbers,
    getNumbers,
    getFormValue: getValues,
    setFormValue: setValue,
    setCurrentResourceNumber,
    setShowAvailableNumbers,
    setShouldCreateAvonUser,
    actingUserRole: actingUser?.role,
    control,
  });

  return (
    <FlexContainer
      direction="column"
      alignItems="flex-start"
      width="100%"
      height="100%"
      overflowY="auto"
      overflowX="hidden"
    >
      <EditUserHeader
        isEditing={isEditing}
        user={user}
        saveDisabled={!isEditing && isAssignableRole && !getValues('role')}
        handleSave={handleSubmit(handleSubmitPressed, handleFormErrors)}
        handleDeleteUser={handleDeletePressed}
        handleBackButton={resetAndNavigate}
      />
      <CreateUserPageContainer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
          }}
        >
          <ResponsiveFieldSection style={{ margin: '8px' }}>
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
            />
          </ResponsiveFieldSection>
          {isEditing && (
            <ResponsiveFieldSection style={{ margin: '8px' }}>
              <VoicemailForm userId={userId} />
            </ResponsiveFieldSection>
          )}
        </div>
        {(!!avonUser || shouldCreateAvonUser) && (
          <FieldSection>
            <EditAvon
              userRole={getValues('role')}
              isAdmin={isAdmin}
              hasAvonUser={hasAvonUser}
              avonUserDetails={avonUserDetails}
              handleAvonUserDetailsUpdated={setAvonUserDetails}
              specializations={avonSpecializations?.['data'] || []}
              medicalCenters={avonMedicalCenters || []}
            />
          </FieldSection>
        )}

        <FieldSection>
          <ManageTeams
            teamOptions={allTeams}
            roleOptions={roleOptions}
            handleTeamsUpdated={teams => setSelectedTeams(teams)}
            selectedTeams={selectedTeams}
            timezone={user?.timeZone}
          />
        </FieldSection>
        {!isGuideOrProvider && (
          <FieldSection>
            <ManageClients
              clientOptions={allClients}
              handleClientsUpdated={clients => setSelectedClients(clients)}
              selectedClients={selectedClients}
              timezone={user?.timeZone}
            />
          </FieldSection>
        )}
        {formErrors.length > 0 &&
          formErrors.map((error, index) => (
            <Typography
              key={`${error}_${index}`}
              sx={{ color: 'red', fontWeight: 'bold' }}
            >
              {error}
            </Typography>
          ))}
      </CreateUserPageContainer>
    </FlexContainer>
  );
};
